/**
 * Mandala Bloom Visualizer - Centered mandala/petal pattern that breathes with music
 *
 * A meditative, calm visualizer using bezier-curve petals arranged radially.
 * Soft gradients, no sharp contrast, no beat flashes. Audio mapping:
 * - Bass → petal length (gentle expansion)
 * - Mids → petal width (breathing width)
 * - Treble → subtle inner detail lines
 *
 * Uses offscreen canvas trail technique for soft bloom/halo that respects
 * the global background controller (solid, gradient, pattern, image).
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity
 * 2. Petals — quadratic bezier curves with radial gradient fill
 * 3. Detail lines — thin inner lines at low alpha (treble-driven)
 * 4. Highlight pass — very faint additive luminosity on petal centers
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'mandala-bloom',
  displayName: 'Mandala Bloom',
  contextType: '2d',
};

class MandalaBloom extends Base2DVisualizer {
  static name = 'mandala-bloom';
  static displayName = 'Mandala Bloom';

  static getConfigUI() {
    return [
      { type: 'range', id: 'petalCount', label: 'Petal Count', min: 12, max: 48, default: 24 },
      { type: 'range', id: 'petalLength', label: 'Petal Length', min: 20, max: 80, default: 50, scale: 0.01 },
      { type: 'range', id: 'petalWidth', label: 'Petal Width', min: 10, max: 60, default: 30, scale: 0.01 },
      { type: 'range', id: 'rotationSpeed', label: 'Rotation Speed', min: 0, max: 20, default: 5, scale: 0.01 },
      { type: 'range', id: 'breathPeriod', label: 'Breath Period', min: 20, max: 100, default: 60, scale: 0.1 },
      { type: 'range', id: 'breathDepth', label: 'Breath Depth', min: 5, max: 50, default: 20, scale: 0.01 },
      { type: 'range', id: 'detailAmount', label: 'Detail Lines', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 3, max: 30, default: 8, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 30, default: 10 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 20, max: 100, default: 70, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 80, scale: 0.01 },
      { type: 'checkbox', id: 'safeMode', label: 'Reduced Motion', default: false },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#ff88cc' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#8844ff' },
    ];
  }

  getDefaultConfig() {
    return {};
  }

  getElementCount() {
    return 64;
  }

  init() {
    // Trail offscreen canvas
    this._trailCanvas = null;
    this._trailCtx = null;
    this.isFirstFrame = true;

    // Animation state
    this._rotationAngle = 0;
    this._breathPhase = 0;

    // Smoothed audio values (per-frame delta-clamped)
    this._smoothedBassLength = 0;
    this._smoothedMidWidth = 0;
    this._smoothedHighDetail = 0;

    // Color cache (parsed RGB from hex)
    this._csR = 0; this._csG = 0; this._csB = 0;
    this._ceR = 0; this._ceG = 0; this._ceB = 0;
    this._cachedCS = '';
    this._cachedCE = '';
    this._ensureColorCache(true);
  }

  onConfigChange(changed) {
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined) {
      this._ensureColorCache(true);
    }
  }

  /**
   * Parse colorStart/colorEnd hex values into RGB components for fast per-frame use.
   */
  _ensureColorCache(force = false) {
    const cs = this.config.colorStart || '#ff88cc';
    const ce = this.config.colorEnd || '#8844ff';

    if (force || this._cachedCS !== cs) {
      this._csR = parseInt(cs.slice(1, 3), 16) || 0;
      this._csG = parseInt(cs.slice(3, 5), 16) || 0;
      this._csB = parseInt(cs.slice(5, 7), 16) || 0;
      this._cachedCS = cs;
    }
    if (force || this._cachedCE !== ce) {
      this._ceR = parseInt(ce.slice(1, 3), 16) || 0;
      this._ceG = parseInt(ce.slice(3, 5), 16) || 0;
      this._ceB = parseInt(ce.slice(5, 7), 16) || 0;
      this._cachedCE = ce;
    }
  }

  /**
   * Ensure the trail offscreen canvas matches the main canvas dimensions.
   */
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Pattern: snapshot previous frame → clearCanvas() for fresh background →
   * composite snapshot at reduced opacity → render new content.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  /**
   * Clamp per-frame delta to prevent jarring jumps.
   */
  _clampDelta(current, target, maxDelta) {
    const delta = target - current;
    if (Math.abs(delta) <= maxDelta) return target;
    return current + Math.sign(delta) * maxDelta;
  }

  /**
   * Main render: petals → detail lines → highlight pass.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const dt = 1 / 60;
    const safe = config.safeMode;

    // Compute band energies from 64-bin amplitude array (3 bands)
    const third = Math.floor(amplitudes.length / 3);
    let bassSum = 0, midSum = 0, highSum = 0;
    for (let i = 0; i < third; i++) bassSum += amplitudes[i];
    for (let i = third; i < third * 2; i++) midSum += amplitudes[i];
    for (let i = third * 2; i < amplitudes.length; i++) highSum += amplitudes[i];
    const bassEnergy = bassSum / third;
    const midEnergy = midSum / third;
    const highEnergy = highSum / (amplitudes.length - third * 2);

    // Per-frame delta clamping for smooth motion
    const maxLenDelta = safe ? 0.01 : 0.02;
    const maxWidDelta = safe ? 0.0075 : 0.015;
    const maxDetDelta = safe ? 0.015 : 0.03;

    this._smoothedBassLength = this._clampDelta(this._smoothedBassLength, bassEnergy, maxLenDelta);
    this._smoothedMidWidth = this._clampDelta(this._smoothedMidWidth, midEnergy, maxWidDelta);
    this._smoothedHighDetail = this._clampDelta(this._smoothedHighDetail, highEnergy, maxDetDelta);

    // Breath animation — slow sinusoidal cycle
    const breathPeriod = config.breathPeriod;
    this._breathPhase += dt * (2 * Math.PI / breathPeriod);
    const breathDepth = safe ? Math.min(config.breathDepth, 0.08) : config.breathDepth;
    const breathMultiplier = 1 + Math.sin(this._breathPhase) * breathDepth;

    // Rotation — constant, extremely slow
    const rotSpeed = safe ? Math.min(config.rotationSpeed, 0.03) : config.rotationSpeed;
    this._rotationAngle += dt * rotSpeed;

    // Canvas center
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const minDim = Math.min(canvas.width, canvas.height);

    // Effective petal dimensions (audio-modulated)
    const basePetalLength = config.petalLength * minDim;
    const basePetalWidth = config.petalWidth * basePetalLength;
    const effectiveLength = basePetalLength * (1 + this._smoothedBassLength * 0.4) * breathMultiplier;
    const effectiveWidth = basePetalWidth * (0.7 + this._smoothedMidWidth * 0.6);

    const petalCount = Math.round(config.petalCount);
    const brightness = safe ? Math.min(config.brightness, 0.6) : config.brightness;
    const glowIntensity = safe ? Math.min(config.glowIntensity, 5) : config.glowIntensity;
    const detailAmount = safe ? Math.min(config.detailAmount, 0.2) : config.detailAmount;

    // --- Layer 1: Petals ---
    for (let i = 0; i < petalCount; i++) {
      const angle = (i / petalCount) * Math.PI * 2 + this._rotationAngle;
      this._drawPetal(cx, cy, angle, effectiveLength, effectiveWidth, i, petalCount, brightness, glowIntensity);
    }

    // --- Layer 2: Detail lines (treble-driven) ---
    if (detailAmount > 0 && this._smoothedHighDetail > 0.01) {
      this._drawDetailLines(cx, cy, effectiveLength, petalCount, detailAmount, brightness);
    }

    // --- Layer 3: Highlight pass (very faint luminosity) ---
    if (brightness > 0.1 && glowIntensity > 0) {
      this._drawHighlightPass(cx, cy, effectiveLength, petalCount, brightness);
    }
  }

  /**
   * Draw a single petal as a closed quadratic bezier path from center.
   *
   * Shape: two quadratic curves forming a leaf shape.
   * - Tip at (cx + cos(θ)*length, cy + sin(θ)*length)
   * - Control points perpendicular to petal axis at ~40% along, spread by width
   */
  _drawPetal(cx, cy, angle, length, width, index, total, brightness, glowIntensity) {
    const { ctx } = this;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Tip of the petal
    const tipX = cx + cos * length;
    const tipY = cy + sin * length;

    // Control point position along the axis (40% from center)
    const cpDist = length * 0.4;
    const cpBaseX = cx + cos * cpDist;
    const cpBaseY = cy + sin * cpDist;

    // Perpendicular direction for width spread
    const perpX = -sin;
    const perpY = cos;

    // Left and right control points
    const cpLeftX = cpBaseX + perpX * width;
    const cpLeftY = cpBaseY + perpY * width;
    const cpRightX = cpBaseX - perpX * width;
    const cpRightY = cpBaseY - perpY * width;

    // Interpolate color for this petal
    const t = index / total;
    const r = Math.round(this._csR + (this._ceR - this._csR) * t);
    const g = Math.round(this._csG + (this._ceG - this._csG) * t);
    const b = Math.round(this._csB + (this._ceB - this._csB) * t);

    // Build the petal path
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.quadraticCurveTo(cpLeftX, cpLeftY, tipX, tipY);
    ctx.quadraticCurveTo(cpRightX, cpRightY, cx, cy);
    ctx.closePath();

    // Radial gradient fill (center to tip)
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, length);
    grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${brightness * 0.8})`);
    grad.addColorStop(0.6, `rgba(${r}, ${g}, ${b}, ${brightness * 0.5})`);
    grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${brightness * 0.15})`);

    ctx.fillStyle = grad;

    // Optional glow via shadowBlur
    if (glowIntensity > 0) {
      ctx.shadowBlur = glowIntensity;
      ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
    }

    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  /**
   * Draw subtle inner detail lines radiating from center (treble-driven).
   * Max 3 detail lines per petal, thin and low alpha.
   */
  _drawDetailLines(cx, cy, petalLength, petalCount, detailAmount, brightness) {
    const { ctx } = this;
    const detailAlpha = Math.min(this._smoothedHighDetail * detailAmount * 0.5, 0.15);
    if (detailAlpha < 0.005) return;

    // Detail lines per petal: 1–3 based on detailAmount
    const linesPerPetal = Math.min(3, Math.max(1, Math.round(detailAmount * 3)));

    ctx.save();
    ctx.strokeStyle = `rgba(255, 255, 255, ${detailAlpha})`;
    ctx.lineWidth = 0.5;

    for (let i = 0; i < petalCount; i++) {
      const baseAngle = (i / petalCount) * Math.PI * 2 + this._rotationAngle;
      const angleStep = (Math.PI * 2 / petalCount) / (linesPerPetal + 1);

      for (let d = 1; d <= linesPerPetal; d++) {
        const angle = baseAngle + angleStep * d;
        const lineLen = petalLength * (0.3 + this._smoothedHighDetail * 0.4);

        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(angle) * lineLen, cy + Math.sin(angle) * lineLen);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  /**
   * Very faint additive highlight on petal center lines for subtle luminosity.
   * Capped at alpha 0.05 * brightness to prevent white blowout through trail accumulation.
   */
  _drawHighlightPass(cx, cy, petalLength, petalCount, brightness) {
    const { ctx } = this;
    const highlightAlpha = 0.05 * brightness;
    if (highlightAlpha < 0.005) return;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = highlightAlpha;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;

    ctx.beginPath();
    for (let i = 0; i < petalCount; i++) {
      const angle = (i / petalCount) * Math.PI * 2 + this._rotationAngle;
      const lineLen = petalLength * 0.7;
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angle) * lineLen, cy + Math.sin(angle) * lineLen);
    }
    ctx.stroke();

    ctx.restore();
  }

  /**
   * Clear canvas and reset all runtime state.
   */
  clear() {
    super.clear();
    this.isFirstFrame = true;
    this._rotationAngle = 0;
    this._breathPhase = 0;
    this._smoothedBassLength = 0;
    this._smoothedMidWidth = 0;
    this._smoothedHighDetail = 0;

    // Clear trail buffer so stale frames don't flash on restart
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(MandalaBloom);

export { MandalaBloom };
export default MandalaBloom;
